'use client';

import { Accordion, AccordionItem, Button, Chip } from '@nextui-org/react';
import { Spinner } from '@nextui-org/spinner';
import { Rate } from '@/components/ui/rate';
import { ChevronLeftIcon, Pencil } from 'lucide-react';
import { PageWrapper } from '@/components/layout/page-wrapper/page-wrapper';
import { McButton } from '@/components/masterclass/mc-button';
import { McLikeBtn } from '@/components/masterclass/mc-like-btn/mc-like-btn';
import { ShareButton } from '@/components/share-button/share-button';
import { SectionHeader } from '@/components/ui/section-header/section-header';
import {
  useGetmasterclassById,
  useGetTop5MasterclassesQuery,
} from '@/entities/masterclass/query';
import { useIsAdmin } from '@/shared/hooks/is-admin';
import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { MobileCarousel } from '@/widgets/masterclass/mobile-carousel';
import { McsList } from '@/widgets/masterclasses/msc-list';
import { McReviewList } from '@/widgets/mc/mc-review-list';
import { useSetHeaderSlot } from '@/context/header-slot-context';
import BlurFade from '@/components/magicui/blur-fade';
import { declension } from '@/shared/utils/declecsion';
import { formatPrice } from '@/shared/utils/format-price';
import { RichBlocks } from '@/shared/ui/rich-blocks';
import { IMasterclass } from '@/entities/masterclass/types';
import { getToySizeFromDescription } from '@/shared/utils/get-toy-size-from-description';

export const Content = ({ masterclass }: { masterclass: IMasterclass }) => {
  const { data: clientMc, isFetching } = useGetmasterclassById(
    masterclass.wordId,
    { initialMasterclass: masterclass },
  );
  const displayMc = clientMc?.wordId ? clientMc : masterclass;
  const isPending = false;
  const { isAdmin } = useIsAdmin();

  const { data: masterclasses } = useGetTop5MasterclassesQuery();

  const size =
    getToySizeFromDescription(displayMc.description) ||
    getToySizeFromDescription(displayMc.contents) ||
    getToySizeFromDescription(displayMc.materials);

  useSetHeaderSlot({
    titleCustom: displayMc?.name ?? 'Мастер-класс',
    rightContent: (
      <div className="flex items-center gap-1">
        {isAdmin && (
          <Link href={`/admin-front/masterclasses/${displayMc.wordId}/edit`}>
            <Button variant="light" isIconOnly radius="full">
              <Pencil strokeWidth={1} size={22} />
            </Button>
          </Link>
        )}
        <ShareButton />
      </div>
    ),
  });

  return (
    <>
      <PageWrapper pending={isPending}>
        {isPending ? (
          <Spinner
            className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
            size="lg"
          />
        ) : (
          <>
            <div className="lg:grid flex flex-col lg:grid-cols-12 gap-4 lg:gap-8 w-full">
              <div className="lg:col-span-7">
                <MobileCarousel
                  mcName={displayMc.name}
                  images={displayMc?.images}
                />
              </div>

              <div className="lg:col-span-5">
                {/*<div className="flex flex-wrap gap-2">*/}
                {/*  {masterclass?.tags?.map((tag) => {*/}
                {/*    return (*/}
                {/*      <Chip color="default" key={tag}>*/}
                {/*        {tag}*/}
                {/*      </Chip>*/}
                {/*    );*/}
                {/*  })}*/}
                {/*</div>*/}
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-start justify-between gap-3 w-full md:mt-4">
                    <h1 className="text-xl lg:text-2xl font-bold w-full text-balance">
                      {displayMc?.name ?? ''} — мастер-класс амигуруми
                      крючком от Марины Камбур
                    </h1>
                    {isAdmin && (
                      <Link
                        href={`/admin-front/masterclasses/${displayMc.wordId}/edit`}
                        className="hidden lg:block shrink-0"
                      >
                        <Button variant="bordered">
                          Редактировать МК <Pencil strokeWidth={1} size={22} />
                        </Button>
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <div className="flex gap-1 text-gray-600 items-center">
                      <Rate
                        style={{ fontSize: '24px' }}
                        readOnly
                        value={displayMc?.rate ?? 0}
                        allowHalf
                      />
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {displayMc?.reviewsCount
                          ? `${declension(displayMc.reviewsCount, [
                              'отзыв',
                              'отзыва',
                              'отзывов',
                            ])}`
                          : 'нет отзывов'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 w-full flex-wrap items-center">
                    <span className="lg:text-2xl text-xl text-gray-900 items-center flex gap-1 font-bold">
                      {displayMc?.finalPrice != null
                        ? formatPrice(displayMc.finalPrice)
                        : ''}{' '}
                      ₽
                    </span>
                    {!!displayMc?.discount && (
                      <p className="lg:text-lg text-base text-muted-foreground line-through ">
                        {displayMc?.price != null
                          ? formatPrice(displayMc.price)
                          : ''}{' '}
                        ₽
                      </p>
                    )}{' '}
                    {!!displayMc?.discount && (
                      <Chip color="danger">
                        –{displayMc?.discount?.toFixed(0)}%
                      </Chip>
                    )}
                    {size && (
                      <Chip className="transform text-white bg-lime-700">
                        Размер: {size} см
                      </Chip>
                    )}
                  </div>
                  <RichBlocks content={displayMc?.description} />
                  {/* keepContentMounted: контент секций остаётся в DOM/SSR —
                      материалы и состав МК индексируются поисковиками */}
                  <Accordion keepContentMounted>
                    <AccordionItem
                      indicator={<ChevronLeftIcon />}
                      key="2"
                      aria-label="Что входит в МК"
                      title="Что входит в МК"
                    >
                      <RichBlocks content={displayMc?.contents} />
                    </AccordionItem>
                    <AccordionItem
                      indicator={<ChevronLeftIcon />}
                      key="1"
                      aria-label="Необходимы материалы"
                      title="Необходимые материалы"
                    >
                      <RichBlocks content={displayMc?.materials} />
                    </AccordionItem>
                    <AccordionItem
                      indicator={<ChevronLeftIcon />}
                      key="3"
                      aria-label="Подробное описание"
                      title="Подробное описание"
                    >
                      <div className="flex flex-col gap-3 text-sm leading-relaxed text-default-600 md:text-base">
                        <p>
                          «{displayMc?.name}» — мой авторский мастер-класс по
                          вязанию игрушки амигуруми крючком. Это подробный PDF
                          с пошаговым описанием каждого ряда, фотографиями
                          процесса и полным списком материалов с расходом пряжи
                          {size
                            ? ` — готовая игрушка получится размером ${size} см`
                            : ''}
                          . Описания подробные, без сокращений и пропущенных
                          рядов — понадобятся базовые навыки вязания крючком:
                          воздушные петли, столбики без накида и вязание по
                          кругу.
                        </p>
                        <p>
                          Мастер-класс стоит{' '}
                          {displayMc?.finalPrice != null
                            ? formatPrice(displayMc.finalPrice)
                            : ''}{' '}
                          ₽. После оплаты файл придёт на вашу почту и останется
                          в личном кабинете — вяжите в удобном темпе и
                          возвращайтесь к описанию в любой момент. Если что-то
                          не получается — напишите мне, я помогу разобраться.
                          Другие игрушки смотрите в{' '}
                          <Link
                            href={ROUTES.masterclasses}
                            className="font-medium text-warning-700 underline-offset-4 hover:underline dark:text-warning-400"
                          >
                            каталоге мастер-классов
                          </Link>
                          .
                        </p>
                      </div>
                    </AccordionItem>
                    {/*<AccordionItem*/}
                    {/*  key="3"*/}
                    {/*  aria-label="Accordion 3"*/}
                    {/*  title="Accordion 3"*/}
                    {/*>*/}
                    {/*  'grgregre'*/}
                    {/*</AccordionItem>*/}
                  </Accordion>
                  {displayMc && (
                    <div className="flex items-center w-full gap-8 justify-between">
                      <McButton
                        className="w-full"
                        primary
                        isLoading={isFetching}
                        masterclass={displayMc}
                        size="lg"
                      />
                      <McLikeBtn custom mc={displayMc} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-5 pb-10 ">
              <BlurFade delay={0.5}>
                <McReviewList masterclass={displayMc} />
              </BlurFade>

              <BlurFade delay={0.5}>
                <div className="col-span-12 flex flex-col gap-4">
                  <SectionHeader
                    title={'Вам может понравиться'}
                    subTitle="Похожие материалы"
                    rightContent={
                      <Link href={ROUTES.masterclasses}>
                        <Button className="text-gray-900">Смотреть все</Button>
                      </Link>
                    }
                  />
                  <McsList
                    isPending={isPending}
                    masterclasses={masterclasses || []}
                  />
                </div>
              </BlurFade>
            </div>
          </>
        )}
      </PageWrapper>
    </>
  );
};
